import React, { useEffect } from 'react';
import { toast } from 'react-toastify';
import { Button, IconButton, TextField, Tooltip } from '@material-ui/core';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import CheckIcon from '@material-ui/icons/Check';
import ClearIcon from '@material-ui/icons/Clear';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import { useStylesForSettings } from '../../../components/SettingsComponents/settingsHelpers';
import SettingsTableHeader from '../../../components/SettingsComponents/SettingsTableHeader';
import { useHardwareMetaData } from '../../../hooks/useHardwareMetaData';
import { AddLocationFormValue } from './addLocation';
import { emptyLocation, UpdateLocation } from './updateLocation';
import { addLocationDtoInitialValues, useAddLocationData } from './useAddLocationData';
import { useDeleteLocationyData } from './useDeleteLocationsData';
import { useUpdateLocationData } from './useUpdateLocationData';

const LocationsList = () => {
  const classes = useStylesForSettings();
  const data = useHardwareMetaData();
  const { submitDeleteLocation } = useDeleteLocationyData();
  const { submitAddLocation } = useAddLocationData();
  const { submitUpdateLocation } = useUpdateLocationData();
  const [locations, setLocations] = React.useState(data.locations);
  const [selected, setSelected] = React.useState(-1);
  const [values, setValues] = React.useState<AddLocationFormValue>(
    addLocationDtoInitialValues,
  );
  const [updatedValues, setUpdatedValues] = React.useState<UpdateLocation>(emptyLocation);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { target } = event;
    const { value } = target;
    const { name } = target;
    setValues({ ...values, [name]: value });
  };

  const handleSubmit = async (event: React.FormEvent<HTMLButtonElement>) => {
    try {
      const res = await submitAddLocation(values);
      if (res) {
        setLocations([...locations, res as { id: number; name: string }]);
        setValues(addLocationDtoInitialValues);
      }
    } catch {
      toast.error('Error adding location');
    }
    event.preventDefault();
  };

  const handleUpdate = async (id: number) => {
    try {
      const resType = await submitUpdateLocation(id, updatedValues);
      if (resType === 'success') {
        setLocations(
          locations.map((b) => (b.id === id ? { ...b, name: updatedValues.name } : b)),
        );
      }
      deactivateEditMode();
    } catch {
      toast.error('Error updating brand');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const resType = await submitDeleteLocation(id);
      if (resType === 'success') {
        setLocations(locations.filter((b) => b.id !== id));
      }
    } catch {
      toast.error('Error deleting brand');
    }
  };

  const handleUpdateChange =
    (name: keyof UpdateLocation) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setUpdatedValues({ ...updatedValues, [name]: event.target.value });
    };

  const handleClick = (id: number) => {
    setSelected(id);
  };

  function deactivateEditMode() {
    setSelected(-1);
  }

  useEffect(() => {
    setLocations(data.locations);
  }, [data.locations]);

  const isSelected = (id: number) => selected === id;

  return (
    <Paper className={classes.paper}>
      <div className={classes.tableWrapper}>
        <Table
          className={classes.table}
          aria-labelledby="tableTitle"
          size="small"
          aria-label="enhanced table"
        >
          <SettingsTableHeader />
          {locations.length === 0 ? (
            <TableRow>
              <TableCell align="center">
                <TextField
                  id="standard-basic"
                  name="name"
                  value={values.name}
                  onChange={handleChange}
                  style={{ width: '20vw' }}
                  multiline
                />
              </TableCell>
              <TableCell align="center">
                <Button
                  className={classes.button}
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={handleSubmit}
                >
                  Add New
                </Button>
              </TableCell>
            </TableRow>
          ) : (
            <TableBody>
              <TableRow>
                <TableCell align="center">
                  <TextField
                    id="standard-basic"
                    name="name"
                    value={values.name}
                    onChange={handleChange}
                    style={{ width: '20vw' }}
                    multiline
                  />
                </TableCell>
                <TableCell align="center">
                  <Button
                    className={classes.button}
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={handleSubmit}
                  >
                    Add New
                  </Button>
                </TableCell>
              </TableRow>
              {locations.map((d) => {
                const isItemSelected = isSelected(d.id);

                return (
                  <TableRow
                    role="checkbox"
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={d.id}
                  >
                    {isItemSelected ? (
                      <TableCell align="center">
                        <TextField
                          id="standard-basic"
                          name="name"
                          defaultValue={d.name}
                          style={{ width: '20vw' }}
                          onChange={handleUpdateChange('name')}
                          multiline
                        />
                      </TableCell>
                    ) : (
                      <TableCell align="center">{d.name}</TableCell>
                    )}
                    {isItemSelected ? (
                      <TableCell align="right">
                        <Tooltip title="save changes">
                          <IconButton
                            onClick={() => handleUpdate(d.id)}
                            className={classes.showDetailsIconButton}
                          >
                            <CheckIcon
                              fontSize="small"
                              className={classes.showDetailsIcon}
                            />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="cancel">
                          <IconButton
                            onClick={() => deactivateEditMode()}
                            className={classes.showDetailsIconButton}
                          >
                            <ClearIcon
                              fontSize="small"
                              className={classes.showDetailsIcon}
                            />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    ) : (
                      <TableCell align="right">
                        <Tooltip title="edit">
                          <IconButton
                            disabled={selected !== -1}
                            onClick={(event) => handleClick(d.id)}
                            className={classes.showDetailsIconButton}
                          >
                            <EditIcon
                              fontSize="small"
                              className={classes.showDetailsIcon}
                            />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="delete">
                          <IconButton
                            className={classes.showDetailsIconButton}
                            onClick={() => handleDelete(d.id)}
                          >
                            <DeleteIcon
                              fontSize="small"
                              className={classes.showDetailsIcon}
                            />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          )}
        </Table>
      </div>
    </Paper>
  );
};

export default LocationsList;
